<template>
  <div>
    <v-dialog
      v-model="show"
      content-class="crud-dialog"
      persistent
      max-width="800px"
    >
      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 v-if="company.id" class="text-h5">Update company</h2>
              <h2 v-else class="text-h5">Add a company</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close ml-md-6"
                color="black"
                @click.stop="closeDialog"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <ValidationObserver ref="observer">
            <v-form class="modal-form">
              <ValidationProvider
                id="trading_name"
                v-slot="{ errors }"
                name="trading_name"
                rules="required"
              >
                <v-text-field
                  v-model="company.tradingName"
                  label="Trading name*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                id="co_branded_banner_title"
                v-slot="{ errors }"
                name="co_branded_banner_title"
              >
                <v-text-field
                  v-model="company.coBrandedBannerTitle"
                  label="Co branded banner title"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                id="co_branded_banner_description"
                v-slot="{ errors }"
                name="co_branded_banner_description"
              >
                <v-text-field
                  v-model="company.coBrandedBannerDescription"
                  label="Co branded banner description"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="company_type"
                v-slot="{ errors }"
                name="company_type"
                rules="required"
              >
                <v-select
                  v-model="company.companyType"
                  :items="$options.companyTypes"
                  label="Company type*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                id="executorship"
                v-slot="{ errors }"
                name="executorship"
                rules="required"
              >
                <v-select
                  v-model="company.executorship"
                  :items="executorships"
                  label="Executorship*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="showNominatedExecutor"
                id="nominated_executor"
                v-slot="{ errors }"
                name="nominated_executor"
                rules="required"
              >
                <v-text-field
                  v-model="company.nominatedExecutor"
                  label="Who will be the nominated Executor where your company is appointed as the Executor for estates?*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                id="offer_trust_administration"
                v-slot="{ errors }"
                name="offer_trust_administration"
                rules="required"
              >
                <v-switch
                  v-model="company.offerTrustAdministration"
                  label="Offer trust administration services to your clients?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="company.offerTrustAdministration"
                id="trust_company_name"
                v-slot="{ errors }"
                name="trust_company_name"
                rules="required"
              >
                <v-text-field
                  v-model="company.trustCompanyName"
                  label="Trust company name*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="company.offerTrustAdministration"
                id="trust_company_reg_no"
                v-slot="{ errors }"
                name="trust_company_reg_no"
                rules="required"
              >
                <v-text-field
                  v-model="company.trustCompanyRegNo"
                  label="Trust company registration number*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <strong class="will-settings-title">Will settings</strong>
              <ValidationProvider
                v-if="isAdmin"
                id="will_payment_option"
                v-slot="{ errors }"
                name="will_payment_option"
                rules="required"
              >
                <v-select
                  v-model="company.willPaymentOption"
                  :items="$options.willPaymentOptions"
                  label="Offer wills for free?*"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <label class="text-subtitle-1"
                >Include other will languages?</label
              >
              <p class="text-caption will-language-description mb-4">
                English is always included. Select any additional languages
                testators can choose when downloading or previewing their will.
              </p>
              <v-checkbox
                v-model="additionalWillLanguages"
                value="af"
                label="Afrikaans"
                color="primary"
                hide-details
                class="company-modal-switch"
              />
              <v-checkbox
                v-model="additionalWillLanguages"
                value="xh"
                label="Xhosa"
                color="primary"
                hide-details
                class="company-modal-switch"
              />
              <v-checkbox
                v-model="additionalWillLanguages"
                value="zu"
                label="Zulu"
                color="primary"
                hide-details
                class="company-modal-switch"
              />
              <v-checkbox
                v-model="additionalWillLanguages"
                value="nso"
                label="Sotho (Sepedi)"
                color="primary"
                hide-details
                class="company-modal-switch will-language-options-last"
              />
              <v-divider class="mt-4 mb-4" />

              <ValidationProvider
                id="negotiable_executor_fee"
                v-slot="{ errors }"
                name="negotiable_executor_fee"
                rules="required"
              >
                <v-switch
                  v-model="company.negotiableExecutorFee"
                  label="Negotiable Executor fee?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                id="default_executor_fee"
                v-slot="{ errors }"
                name="default_executor_fee"
                rules="required|decimal|max_value:3.5"
              >
                <v-text-field
                  v-model="company.defaultExecutorFeePercentage"
                  label="Default Executor fee*"
                  class="default-executor-fee-txt-field"
                  suffix="%"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                id="executor_fee_location"
                v-slot="{ errors }"
                name="executor_fee_location"
                rules="required"
              >
                <label class="text-subtitle-1"
                  >Where should your agreed estate admin fee be
                  displayed?*</label
                >
                <v-radio-group
                  v-model="company.executorFeeLocation"
                  row
                  :error-messages="errors"
                >
                  <v-radio
                    label="In a side agreement"
                    value="SIDE_AGREEMENT"
                    color="primary"
                  />
                  <v-radio label="In the will" value="WILL" color="primary" />
                </v-radio-group>
              </ValidationProvider>

              <strong>Insurance Settings</strong>
              <br />

              <ValidationProvider
                v-if="isAdmin"
                id="insurance_step"
                v-slot="{ errors }"
                name="insurance_step"
                rules="required"
              >
                <v-select
                  v-model="company.insuranceStep"
                  :items="$options.insuranceSteps"
                  label="Insurance step*"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <ValidationProvider
                v-if="isAdmin"
                id="campaign_code"
                v-slot="{ errors }"
                name="campaign_code"
              >
                <v-text-field
                  v-model="company.campaignCode"
                  label="DifferentTech campaign code"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <ValidationProvider
                v-if="isAdmin"
                id="campaign_assisted_code"
                v-slot="{ errors }"
                name="campaign_assisted_code"
              >
                <v-text-field
                  v-model="company.campaignAssistedCode"
                  label="DifferentTech assisted campaign code"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <strong>1Stream Settings</strong>
              <br />

              <ValidationProvider
                v-if="isAdmin"
                id="one_stream_team_id"
                v-slot="{ errors }"
                name="one_stream_team_id"
              >
                <v-text-field
                  v-model="company.oneStreamTeamId"
                  label="1Stream Team ID"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="one_stream_tag_id"
                v-slot="{ errors }"
                name="one_stream_tag_id"
              >
                <v-text-field
                  v-model="company.oneStreamTagId"
                  label="1Stream Tag ID"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="one_stream_insurance_tag_id"
                v-slot="{ errors }"
                name="one_stream_insurance_tag_id"
              >
                <v-text-field
                  v-model="company.oneStreamInsuranceTagId"
                  label="1Stream Insurance Tag ID"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="one_stream_utm_source"
                v-slot="{ errors }"
                name="one_stream_utm_source"
              >
                <v-text-field
                  v-model="company.oneStreamUtmSource"
                  label="1Stream UTM Source"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="one_stream_url"
                v-slot="{ errors }"
                name="one_stream_url"
              >
                <v-text-field
                  v-model="company.oneStreamUrl"
                  label="1Stream URL"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="spp_one_stream_id"
                v-slot="{ errors }"
                name="one_stream_spp_id"
              >
                <v-text-field
                  v-model="company.sppOneStreamId"
                  label="1Stream Spp ID"
                  :error-messages="errors"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="refer_a_friend"
                v-slot="{ errors }"
                name="refer_a_friend"
                rules="required"
              >
                <v-switch
                  v-model="company.referAFriend"
                  label="Enable refer a friend?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="will_upload_reminder_email"
                v-slot="{ errors }"
                name="will_upload_reminder_email"
                rules="required"
              >
                <v-switch
                  v-model="company.willUploadReminderEmail"
                  label="Enable reminder emails, sent to advisor after 30 days, if testator has not uploaded signed will?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="hide_quickwill_logo_on_will"
                v-slot="{ errors }"
                name="hide_quickwill_logo_on_will"
                rules="required"
              >
                <v-switch
                  v-model="company.hideQuickwillLogoOnWill"
                  :label="`Hide ${whitelabel.name} logo on will documents?`"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="contact_center_workflow"
                v-slot="{ errors }"
                name="contact_center_workflow"
                rules="required"
              >
                <v-switch
                  v-model="company.contactCenterWorkflow"
                  label="Enable contact center workflow?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="advisor_appointment"
                v-slot="{ errors }"
                name="advisor_appointment"
                rules="required"
              >
                <v-switch
                  v-model="company.advisorAppointment"
                  label="Enable advisor appointment?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>
              <ValidationProvider
                v-if="isAdmin"
                id="commission"
                v-slot="{ errors }"
                name="commission"
              >
                <v-switch
                  v-model="company.commission"
                  label="Enable advisor commission?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="rather-chat-integration"
                v-slot="{ errors }"
                name="rather-chat-integration"
              >
                <v-switch
                  v-model="company.ratherChatEnabled"
                  label="Enable Rather.Chat integration?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <ValidationProvider
                v-if="isAdmin"
                id="speak-to-dfa"
                v-slot="{ errors }"
                name="speak-to-dfa"
              >
                <v-switch
                  v-model="company.speakToDfaAdvisorEnabled"
                  label="Enable Speak to DFA advisor?"
                  :error-messages="errors"
                  class="company-modal-switch"
                />
              </ValidationProvider>

              <v-text-field
                v-model="company.address"
                label="Address where will should be delivered for storage"
              />

              <v-file-input
                v-model="uploadedImage"
                single-line
                outlined
                filled
                accept="image/*"
                label="Upload new logo"
                class="file-upload-btn"
              />
              <a
                v-if="company.logoUrl"
                :href="company.logoUrl"
                target="_blank"
                class="current-company-logo-link"
                >View current logo</a
              >

              <v-select
                v-if="isAdmin"
                v-model="company.commissionScheme"
                :items="commissionSchemeOptions"
                item-text="text"
                item-value="value"
                label="Commission Scheme"
                hint="Which submission file this company's advisor referrals are written to."
                persistent-hint
                class="mb-4"
              />

              <object-commission-structure
                v-if="isAdmin"
                :object_id="company.id"
                object_type="Company"
                :initial-commission-structures="
                  company.objectCommissionPairs || []
                "
                @commission-data-updated="handleCommissionDataUpdate"
              />
            </v-form>
          </ValidationObserver>
        </v-card-text>

        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col cols="12">
              <v-btn
                rounded
                large
                depressed
                :loading="buttonLoading"
                color="primary"
                class="ml-4 mb-1"
                @click="manageBranches()"
              >
                <span>Manage branches</span>
                <v-icon small>{{ icons.mdiArrowRight }}</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>

        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col cols="12">
              <v-btn
                rounded
                large
                depressed
                :loading="buttonLoading"
                color="primary"
                class="ml-4 mb-1"
                @click="save()"
              >
                <span>Save</span>
                <v-icon small>{{ icons.mdiArrowRight }}</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </div>
</template>

<script>
  import {
    CREATE_OR_UPDATE_COMPANY_MUTATION,
    COMPANIES_QUERY,
  } from '../../../constants/graphql/companies';
  import ErrorSnackbar from '../../common/ErrorSnackbar.vue';
  import SuccessSnackbar from '../../common/SuccessSnackbar.vue';
  import { formHelperMixin } from '../../../mixins/form-helper-mixin';
  import ObjectCommissionStructure from '../commission/ObjectCommissionStructure.vue';
  import { commissionSchemeOptions } from '../../../constants/js/commission-schemes';

  export default {
    name: 'CompanyDialog',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
      ObjectCommissionStructure,
    },
    mixins: [formHelperMixin],
    props: {
      value: Boolean,
      companyToUpdate: {
        type: Object,
        default: () => {
          return {};
        },
      },
    },
    data() {
      return {
        company: this.companyDefaults(),
        errorMessage: null,
        showSuccess: false,
        successMessage: null,
        uploadedImage: null,
        commissionData: {
          objectCommissionPairs: [],
        },
        commissionSchemeOptions,
      };
    },
    companyTypes: [
      { text: 'Financial Advisor company', value: 'FINANCIAL_ADVISOR' },
      { text: 'Referral company', value: 'REFERRAL' },
      {
        text: 'Auto assign company (new testators get auto assigned to consultants)',
        value: 'AUTO_ASSIGN',
      },
    ],
    insuranceSteps: [
      { text: 'No insurance', value: 'NO_INSURANCE' },
      { text: 'Liquidity calculator', value: 'LIQUIDITY_CALCULATOR' },
      { text: 'Full insurance', value: 'FULL_INSURANCE' },
      { text: 'Advisor full insurance', value: 'ADVISOR_FULL_INSURANCE' },
      { text: 'OMP Financial Data SQ', value: 'OMP_FINANCIAL_DATA_SQ' },
    ],
    willPaymentOptions: [
      { text: 'Always free', value: 'ALWAYS_FREE' },
      { text: 'First will free', value: 'FIRST_WILL_FREE' },
      { text: 'Not free', value: 'NOT_FREE' },
    ],
    computed: {
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      executorships() {
        return [
          {
            text: `${this.whitelabel.name} or user selected nomination`,
            value: 'INTERNAL_COMPANY_OR_PERSON',
          },
          {
            text: 'Our company or user selected nomination',
            value: 'COMPANY_OR_PERSON',
          },
          { text: 'Only our company', value: 'COMPANY_ONLY' },
          {
            text: `${this.whitelabel.name} only`,
            value: 'INTERNAL_COMPANY_ONLY',
          },
        ];
      },
      isAdmin() {
        return this.$store.getters.isAdmin;
      },
      isEdit() {
        return this.company.id;
      },
      showNominatedExecutor() {
        return (
          this.company.executorship == 'COMPANY_OR_PERSON' ||
          this.company.executorship == 'COMPANY_ONLY'
        );
      },
      additionalWillLanguages: {
        get() {
          const langs = this.company.willDownloadLanguages || [];
          return langs.filter(l => l !== 'en');
        },
        set(val) {
          this.company.willDownloadLanguages = ['en', ...(val || [])];
        },
      },
    },
    watch: {
      value: function (dialogOpen) {
        if (dialogOpen) {
          // using native JSON functions removes reactivity
          // so we can clone an object without mutating the original source
          if (this.companyToUpdate.id) {
            this.company = JSON.parse(JSON.stringify(this.companyToUpdate));
          }
        }
      },
    },
    methods: {
      companyDefaults() {
        return {
          offerTrustAdministration: false,
          negotiableExecutorFee: false,
          willUploadReminderEmail: true,
          referAFriend: false,
          hideQuickwillLogoOnWill: false,
          contactCenterWorkflow: false,
          willDownloadLanguages: ['en'],
        };
      },
      save() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            if (this.uploadedImage) {
              this.createOrUpdateCompanyWithImage();
            } else {
              this.createOrUpdateCompany();
            }
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      manageBranches() {
        this.$router.push({
          name: 'BranchesPage',
          params: { companyId: this.companyToUpdate.id },
        });
      },
      createOrUpdateCompanyWithImage() {
        var reader = new FileReader();
        reader.readAsDataURL(this.uploadedImage);
        reader.onload = function (evt) {
          const logoBase64 = evt.target.result;
          this.createOrUpdateCompany(logoBase64);
        }.bind(this);
      },
      createOrUpdateCompany(logoBase64 = null) {
        const companyInputDetails = this.companyInputDetails(logoBase64);
        const id = this.company.id;

        this.$apollo
          .mutate({
            mutation: CREATE_OR_UPDATE_COMPANY_MUTATION,
            variables: {
              id,
              companyInputDetails,
            },
            update: (cache, { data: { createOrUpdateCompany } }) => {
              this.updateCacheAfterCreateOrUpdateCompany(
                cache,
                createOrUpdateCompany
              );
            },
          })
          .then(_response => {
            this.successMessage = this.isEdit
              ? 'Company successfully updated'
              : 'Company successfully created';
            this.showSuccess = true;
            this.commissionData = { objectCommissionPairs: [] };
            this.closeDialog();
          })
          .catch(error => {
            if (error.message.includes('GraphQL error:')) {
              this.errorMessage = error.message
                .replace('GraphQL error:', '')
                .trim();
              this.displayErrorAndCloseDialog();
            } else {
              this.displayErrorAndCloseDialog();
              throw error;
            }
          });
      },
      updateCacheAfterCreateOrUpdateCompany(cache, createOrUpdateCompany) {
        try {
          const query = {
            query: COMPANIES_QUERY,
          };
          const data = cache.readQuery(query);

          if (this.isEdit) {
            var company = data.companies.find(
              company => company.id == createOrUpdateCompany.id
            );
            var company = createOrUpdateCompany;
          } else {
            data.companies.push(createOrUpdateCompany);
          }

          cache.writeQuery({ ...query, data: data });
        } catch (err) {
          if (err.name == 'Invariant Violation') {
            console.log('COMPANIES_QUERY not in cache store yet.');
          } else {
            throw err;
          }
        }
      },
      handleCommissionDataUpdate(data) {
        this.commissionData = data;
      },
      companyInputDetails(logoBase64) {
        var companyInputDetails = {
          tradingName: this.company.tradingName,
          executorship: this.company.executorship,
          offerTrustAdministration: this.company.offerTrustAdministration,
          negotiableExecutorFee: this.company.negotiableExecutorFee,
          defaultExecutorFeePercentage: parseFloat(
            this.company.defaultExecutorFeePercentage
          ),
          executorFeeLocation: this.company.executorFeeLocation,
          address: this.company.address,
        };
        if (this.isAdmin) {
          companyInputDetails.willPaymentOption =
            this.company.willPaymentOption;
          companyInputDetails.insuranceStep = this.company.insuranceStep;
          companyInputDetails.companyType = this.company.companyType;
          companyInputDetails.referAFriend = this.company.referAFriend;
          companyInputDetails.willUploadReminderEmail =
            this.company.willUploadReminderEmail;
          companyInputDetails.hideQuickwillLogoOnWill =
            this.company.hideQuickwillLogoOnWill;
          companyInputDetails.contactCenterWorkflow =
            this.company.contactCenterWorkflow;
          companyInputDetails.campaignCode = this.company.campaignCode;
          companyInputDetails.campaignAssistedCode =
            this.company.campaignAssistedCode;
          companyInputDetails.advisorAppointment =
            this.company.advisorAppointment;
          companyInputDetails.oneStreamTeamId = this.company.oneStreamTeamId;
          companyInputDetails.oneStreamTagId = this.company.oneStreamTagId;
          companyInputDetails.oneStreamInsuranceTagId =
            this.company.oneStreamInsuranceTagId;
          companyInputDetails.oneStreamUtmSource =
            this.company.oneStreamUtmSource;
          companyInputDetails.oneStreamUrl = this.company.oneStreamUrl;
          companyInputDetails.coBrandedBannerTitle =
            this.company.coBrandedBannerTitle;
          companyInputDetails.coBrandedBannerDescription =
            this.company.coBrandedBannerDescription;
          companyInputDetails.commission = this.company.commission;
          companyInputDetails.commissionScheme = this.company.commissionScheme;
          companyInputDetails.objectCommissionPairsAttributes =
            this.commissionData.objectCommissionPairs;
          companyInputDetails.ratherChatEnabled =
            this.company.ratherChatEnabled;
          companyInputDetails.speakToDfaAdvisorEnabled =
            this.company.speakToDfaAdvisorEnabled;
          companyInputDetails.sppOneStreamId = this.company.sppOneStreamId;
        }
        companyInputDetails.willDownloadLanguages = [
          ...new Set(['en', ...(this.company.willDownloadLanguages || [])]),
        ];
        if (logoBase64) {
          companyInputDetails.logoBase64 = logoBase64;
        }
        companyInputDetails.nominatedExecutor = this.showNominatedExecutor
          ? this.company.nominatedExecutor
          : null;
        companyInputDetails.trustCompanyName = this.company
          .offerTrustAdministration
          ? this.company.trustCompanyName
          : null;
        companyInputDetails.trustCompanyRegNo = this.company
          .offerTrustAdministration
          ? this.company.trustCompanyRegNo
          : null;

        return companyInputDetails;
      },
      closeDialog() {
        this.show = false;
        setTimeout(() => this.clearDialog(), 300);
      },
      clearDialog() {
        this.buttonLoading = false;
        this.company = this.companyDefaults();
        this.uploadedImage = null;
        if (this.$refs.observer) {
          this.$refs.observer.reset();
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .modal-close {
    float: right;
  }

  .default-executor-fee-txt-field {
    max-width: 200px;
  }

  .file-upload-btn {
    display: inline-flex;
  }

  .current-company-logo-link {
    color: grey;
    margin-left: 40px;
  }

  .will-language-description {
    color: rgba(0, 0, 0, 0.7);
  }

  .will-settings-title {
    display: block;
    margin-bottom: 16px;
  }

  .will-language-options-last {
    margin-bottom: 20px;
  }
</style>
